import './TenantBillingInvoiceListPage.css'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  tenantBillingInvoiceListSpec,
  tenantBillingInvoicePdfGetSpec,
  type BillingInvoiceRow,
  type BillingInvoicePaymentData,
  BillingInvoiceStatus,
  tenantGetSpec,
  type TenantRow,
  type TenantExternalIds,
  tenantBillingPortalLinkSpec,
  tenantBillingInvoicePaymentInitiateSpec,
} from 'aevocam-rest-api-spec'
import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { BasicPage } from '@/components/layout/BasicPage'
import { Button } from '@/components/action/Button'
import { IconButton } from '@/components/action/IconButton'
import { SelectBox } from '@/components/scalar/SelectBox'
import {
  startLoading,
  doneLoading,
  type DataGridState,
} from '@/components/dataGrid/createDataGridState'
import { DataGrid, type DataGridColumn } from '@/components/dataGrid/DataGrid'
import { formatDecimal, asLocalDate, toFullDate, toShortDate } from '@/utilities/formatters'
import { toastError } from '@/components/action/Toaster'
import type { GlobalStateSetter } from '@/utilities/createGlobalState'
import { cn } from '@/utilities/misc'
import { Notice } from '@/components/action/Notice'
import { TenantBillingStatus } from 'aevocam-rest-api-spec'
import { TextBox } from '@/components/scalar/TextBox'
import { tenantBillingStatusNames } from './tenantBillingStatusNames'
import { TenantBillingPlan } from '../edit/TenantBillingPlan'

const PAGE_SIZE = 100

export function TenantBillingInvoiceListPage() {
  const { tenant: tenantId } = useParams<{ tenant: string }>()
  const api = useApi({ requireLogin: true })

  const currentYear = new Date().getFullYear()
  const [yearOptions, setYearOptions] = useState<{ label: string; value: number }[]>([
    { label: String(currentYear), value: currentYear },
  ])
  const [year, setYear] = useState(currentYear)
  const [tenant, setTenant] = useState<TenantRow>()

  const [pageState, setPageState] = useState<DataGridState<BillingInvoiceRow>>({
    rows: [],
    totalRows: 0,
    skip: 0,
    take: PAGE_SIZE,
    refresh: 1,
  })

  const externalIds = (tenant?.externalIds ?? undefined) as
    TenantExternalIds | undefined

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (!tenantId) return
    const y = new Date().getFullYear()
    setYear(y)
    setYearOptions([{ label: String(y), value: y }])
    setPageState({
      rows: [],
      totalRows: 0,
      skip: 0,
      take: PAGE_SIZE,
      refresh: 1,
    })
  }, [tenantId])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (!tenantId) return
    let cancelled = false

    async function fn() {

      try {
        const res = await api.request(tenantGetSpec, {
          path: { tenant: tenantId! },
        })
        if (cancelled) return
        if (res.status !== 200) {
          toastError(res.error ?? 'Failed to load tenant info')
        } else {
          setTenant(res.data?.tenant)
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) toastError('Failed to load tenant info')
      }

      // To get the earliest invoice year, do a reverse-order search
      try {
        const res = await api.request(tenantBillingInvoiceListSpec, {
          path: { tenant: tenantId! },
          query: {
            sortReverse: 'true',
            take: '1',
            skip: '0',
          },
        })
        if (cancelled) return
        if (res.status !== 200) {
          toastError(res.error ?? 'Failed to load invoice range')
        } else {
          const row = res.data?.invoices?.rows?.[0]
          const oldestY = calendarYearFromInvoice(row)
          const nowY = new Date().getFullYear()
          const minY = oldestY ?? nowY
          const opts: { label: string; value: number }[] = []
          for (let y = nowY; y >= minY; y--)
            opts.push({ label: String(y), value: y })
          setYearOptions(opts)
          setYear(prev => (opts.some(o => o.value === prev) ? prev : (opts[0]?.value ?? nowY)))
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) toastError('Failed to load invoice range')
      }

    }
    void fn()

    return () => {
      cancelled = true
    }
  }, [tenantId, api])

  //--------------------------------------------------------------------------------
  // Paged list for the selected calendar year.
  useEffect(() => {
    if (!tenantId) return
    let cancelled = false

    async function fn() {
      startLoading(setPageState, pageState.refreshQuietly)
      try {
        const refreshIncrement = pageState.refresh ?? 0
        const res = await api.request(tenantBillingInvoiceListSpec, {
          path: { tenant: tenantId! },
          query: {
            fromDate: `${year}-01-01`,
            toDate: `${year}-12-31`,
            skip: String(pageState.skip),
            take: String(pageState.take),
          },
        })
        if (cancelled) return
        if (res.status === 200 && res.data?.invoices) {
          setPageState(prev => {
            if (prev.refresh !== refreshIncrement) return prev
            return {
              ...prev,
              rows: res.data?.invoices?.rows ?? [],
              totalRows: res.data?.invoices?.totalRows ?? 0,
            }
          })
        } else if (!cancelled) {
          toastError(res.error ?? 'Failed to load invoices')
        }
      } catch {
        if (!cancelled) toastError('Failed to load invoices')
      } finally {
        if (!cancelled) doneLoading(setPageState)
      }
    }
    void fn()

    return () => {
      cancelled = true
    }
  }, [
    tenantId,
    year,
    pageState.skip,
    pageState.take,
    pageState.refresh,
    api,
  ])

  //--------------------------------------------------------------------------------
  const openInvoicePdf = useCallback(async (invoiceId: BillingInvoiceRow['id']) => {
    if (invoiceId == null || !tenantId) return

    // Blank tab must open synchronously (user gesture). After `await`, `window.open` is unreliable;
    // with `noopener` in the features string, some browsers return null even when a tab opens.
    const tab = window.open('about:blank', 'pdf-viewer')
    if (!tab) {
      toastError('Could not open a new tab. If a popup blocker is enabled, allow this site to open popups.')
      return
    }

    try {
      const res = await api.requestBlob(tenantBillingInvoicePdfGetSpec, {
        path: {
          tenant: tenantId,
          invoice: String(invoiceId),
        },
      })
      if (res.status === 200 && res.data) {
        const objectUrl = URL.createObjectURL(res.data)
        tab.location.href = objectUrl
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 300_000)
        return
      }
      tab.close()
      toastError(res.error ?? 'Could not load PDF')

    } catch {
      tab.close()
      toastError('Could not load PDF')
    }
  }, [api, tenantId])

  //--------------------------------------------------------------------------------
  const showInvoicePayments = useCallback((row: BillingInvoiceRow) => {
    const paymentData = row.paymentData as BillingInvoicePaymentData
    const payments = paymentData?.stripe?.payments ?? []
    if (payments.length === 0) {
      alert('No payment records found for this invoice.')
      return
    }

    const lines = payments.map((payment, index) => {
      const paidAt = new Date(payment.date!).toLocaleString()
      const amount = formatPaymentAmount(payment.amount, payment.currency)
      const status = payment.status ?? '—'
      const error = formatPaymentError(payment)
      return [
        `#${index + 1}: ${paidAt}`,
        `Amount: ${amount}`,
        `Status: ${status}`,
        `Errors: ${error}`,
      ].join('\n')
    })

    //TODO: Transform this into a proper dialog component
    alert(lines.join('\n\n----------\n\n'))
  }, [])

  //--------------------------------------------------------------------------------
  async function handleMakePayment(row: BillingInvoiceRow) {
    const res = await api.request(tenantBillingInvoicePaymentInitiateSpec, {
      path: {
        tenant: tenantId!,
        invoice: String(row.id),
      },
      body: {
        returnUrl: window.location.href,
        setAsDefault: true,
      },
    })
    if (res.status === 200) {
      window.location.href = res?.data?.paymentPortalUrl!
    } else {
      toastError(res.error ?? 'Failed to initiate payment')
    }
  }

  //--------------------------------------------------------------------------------
  const columns = useMemo<DataGridColumn<BillingInvoiceRow>[]>(() => [
    {
      id: 'id',
      label: 'PDF',
      align: 'left',
      render: (_v, { row }) => (
        <IconButton
          icon='file'
          tooltip='View PDF'
          aria-label='View PDF'
          disabled={row.id == null}
          onClick={() => void openInvoicePdf(row.id)}
        />
      ),
    },
    {
      id: 'id',
      label: 'ID',
      align: 'left',
      render: (value: string) => tenantId + ':' + String(value),
    },
    {
      id: 'month',
      label: 'Month',
      align: 'left',
      render: (_value: string, gridRow) => {
        let monthStart = asLocalDate(String(gridRow.row.monthStart))!
        let monthEnd = new Date(monthStart)
        monthEnd.setMonth(monthEnd!.getMonth() + 1)
        monthEnd.setDate(monthEnd!.getDate() - 1)
        const output = toShortDate(monthStart) + ' - ' + toShortDate(monthEnd)
        return output
      },
    },
    {
      id: 'grossAmount',
      label: 'Total',
      align: 'left',
      render: (v: number) => formatUsd(v),
    },
    {
      id: 'postedOn',
      label: 'Posted',
      align: 'left',
      render: (value: string) => toFullDate(asLocalDate(value)) ?? '—',
    },
    {
      id: 'dueOn',
      label: 'Due',
      align: 'left',
      render: (value: string) => toFullDate(asLocalDate(value)) ?? '—',
    },
    {
      id: 'status',
      label: 'Status',
      align: 'left',
      className: (value: BillingInvoiceStatus) => cn(
        'Status',
        'NoWrap',
        billingInvoiceStatusSentiment(value),
      ),
      render: (value: BillingInvoiceStatus) => billingInvoiceStatusLabel(value),
    },
    {
      id: 'grossPaid',
      label: 'Paid',
      align: 'left',
      render: (v: number) => formatUsd(v),
    },
    {
      id: 'id',
      label: 'Payments',
      align: 'left',
      render: (_value, { row }) => (<>
        <IconButton
          icon='fileInvoiceDollar'
          tooltip='View payments'
          onClick={() => showInvoicePayments(row)}
        />
        {row.status !== BillingInvoiceStatus.Paid && (
          <IconButton
            icon='plus'
            tooltip='Make a payment'
            onClick={() => handleMakePayment(row)}
          />
        )}
      </>),
    },
  ], [openInvoicePdf, showInvoicePayments])

  //--------------------------------------------------------------------------------
  async function handleBillingPortal() {
    const res = await api.request(tenantBillingPortalLinkSpec , {
      path: {
        tenant: tenantId!,
      },
      query: {
        returnUrl: window.location.href,
        createCustomer: 'true',
      }
    })
    const portalUrl = res.data?.billingPortalUrl
    if (res.status === 200 && portalUrl) {
      window.location.href = portalUrl
    }
  }

  //--------------------------------------------------------------------------------
  if (!tenantId) return <main><p>Missing tenant</p></main>

  return (
    <BasicPage
      title='Billing invoices'
      className='TenantBillingInvoiceListPage'
      toolbarButtons={
        <Button
          label='Home'
          icon='chevronLeft'
          href={routes.home(tenantId)}
        />
      }
    >

      <div className='FlexBricks'>

        <TenantBillingPlan tenantId={tenantId} />

        <div
          className='Cutout Wrapped'
          style={{ maxWidth: '30rem' }}
        >
          <h2>Payment settings</h2>

          {tenant?.billingStatus === TenantBillingStatus.Unbillable && (
            <Notice variant='warning'>
              You are not yet enrolled for automatic billing. You 
              must enroll in order to properly use Aevocam.
            </Notice>
          )}

          <TextBox
            label='Payment mode'
            readOnly
            value={tenant?.billingStatus != null
              ? tenantBillingStatusNames[tenant.billingStatus]
              : '-'
            }
          />

          {tenant?.billingStatus === TenantBillingStatus.Unbillable && (<>
            <Button
              label='Enroll'
              icon='fileInvoiceDollar'
              onClick={handleBillingPortal}
            />
          </>)}

          {tenant?.billingStatus === TenantBillingStatus.AutoPay && (<>
            <TextBox
              label='Via'
              readOnly
              width='5rem'
              value='Stripe'
            />
            <TextBox
              label='Billing account holder'
              readOnly
              width='20rem'
              value={externalIds?.stripe?.customerName ?? '—'}
            />
            <TextBox
              label='Email address for billing messages'
              readOnly
              width='20rem'
              value={externalIds?.stripe?.customerEmail ?? '—'}
            />
            <Button
              label={`Stripe's billing portal`}
              icon='externalLink'
              onClick={handleBillingPortal}
            />
          </>)}

        </div>

        <div className='Cutout'>
          <div className='FlexRow ItemsEnd FlexWrap Gap2 Toolbar'>
            <div className='Wrapped FlexGrow'>
              <h2 className='FlexRow ItemsCenter Gap10'>
                Invoices
                <SelectBox
                  prefix='Year'
                  options={yearOptions}
                  valueToString={v => String(v)}
                  valueFromString={v => Number(v)}
                  value={year}
                  setValue={nextYear => {
                    setYear(nextYear)
                    setPageState(prev => ({ ...prev, skip: 0 }))
                  }}
                />
              </h2>
            </div>
          </div>

          <DataGrid
            state={pageState}
            setState={setPageState as GlobalStateSetter<DataGridState<BillingInvoiceRow>>}
            idColumn='id'
            columns={columns}
          />
        </div>

      </div>

    </BasicPage>
  )
}

//--------------------------------------------------------------------------------
function calendarYearFromInvoice(row: BillingInvoiceRow | undefined): number | null {
  const raw = row?.postedOn ?? row?.monthStart ?? row?.createdAt
  if (raw == null) return null
  const d = typeof raw === 'string' ? new Date(raw) : raw
  const y = d.getFullYear()
  return Number.isFinite(y) ? y : null
}

//--------------------------------------------------------------------------------
function formatUsd(value: number | undefined) {
  if (value == null) return '—'
  const t = formatDecimal(value, 2, true)
  return t != null ? `$${t}` : '—'
}

//--------------------------------------------------------------------------------
function billingInvoiceStatusLabel(value: BillingInvoiceStatus) {
  switch (value) {
    case BillingInvoiceStatus.Draft: return 'Draft'
    case BillingInvoiceStatus.Posted: return 'Posted'
    case BillingInvoiceStatus.PaymentPending: return 'Payment pending'
    case BillingInvoiceStatus.Paid: return 'Paid'
    case BillingInvoiceStatus.PartiallyPaid: return 'Partially paid'
    case BillingInvoiceStatus.PaymentFailed: return 'Payment failed'
    case BillingInvoiceStatus.Overdue: return 'Overdue'
    case BillingInvoiceStatus.Defaulted: return 'Defaulted'
    case BillingInvoiceStatus.Voided: return 'Voided'
    default: return `Unknown (${value})`
  }
}

//--------------------------------------------------------------------------------
function billingInvoiceStatusSentiment(value: BillingInvoiceStatus) {
  if (value >= 10) return 'Negative'
  if (value === BillingInvoiceStatus.PaymentPending) return 'Warning'
  if (value === BillingInvoiceStatus.Paid) return 'Positive'
  return 'Neutral'
}

//--------------------------------------------------------------------------------
function formatPaymentAmount(
  amountMinor: number | null | undefined,
  currency: string | null | undefined
): string {
  if (amountMinor == null || !Number.isFinite(amountMinor)) return '—'
  const amountMajor = amountMinor / 100
  const code = (currency ?? 'usd').toUpperCase()
  const localeCurrency = code.length === 3 ? code : 'USD'
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: localeCurrency,
  }).format(amountMajor)
}

//--------------------------------------------------------------------------------
function formatPaymentError(payment: {
  errorMessage?: string | null
  errorCode?: string | null
  declineCode?: string | null
}): string {
  const parts = [payment.errorMessage, payment.errorCode, payment.declineCode]
    .filter((part): part is string => !!part && part.trim() !== '')
  return parts.length > 0 ? parts.join(' | ') : '—'
}
